import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ManagePeopleTable } from './components/ManagePeopleTable';
import { ManagePeopleActions } from './components/ManagePeopleActions';
import { ManagePeopleEmptyState } from './components/ManagePeopleEmptyState';
import EnterpriseContext from 'router/subrouters/Dash/subrouters/private/subrouters/Enterprise/providers/Enterprise/Enterprise.context';
import EnterpriseMembersProvider from 'providers/EnterpriseMembers';
import EnterpriseMembersContext from 'providers/EnterpriseMembers/EnterpriseMembers.context';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import ManageColumns from 'components/ManageColumns';
import {
  HEADING_TRANSLATIONS,
  REQUIRED_COLUMNS,
  TABLE_COLUMNS,
} from './components/ManagePeopleTable/data';

import './ManagePeople.styles.scss';
import './ManagePeople.styles.responsive.scss';

type ManagePeopleProps = {
  className?: string;
};

const ManagePeople: React.FC<ManagePeopleProps> = (props) => {
  const { className } = props;

  const { enterprise } = useContext(EnterpriseContext);
  const { isEmpty } = useContext(EnterpriseMembersContext);

  const { brandName } = enterprise || {};

  const classes = classNames('manage-people', className);

  const { t } = useTranslation();

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onReorder,
    onColumnSizingChange,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(
    TABLES_WITH_COLUMN_SETTINGS.ENTERPRISE_MANAGE_PEOPLE,
    TABLE_COLUMNS,
  );

  const manageColumns = useMemo(
    () => allColumns.filter((column) => !REQUIRED_COLUMNS.includes(column)),
    [allColumns],
  );

  return (
    <div className={classes}>
      <div>
        <h1>{t('manageEnterprisePeople', { brandName })}</h1>

        {!isEmpty && (
          <ManagePeopleActions
            showLimit
            manageColumns={
              <ManageColumns
                allColumns={manageColumns}
                hiddenColumns={hiddenColumns}
                headingTranslations={HEADING_TRANSLATIONS}
                isDefaultColumns={isDefaultColumns}
                onSave={saveColumnSettings}
                onResetToDefault={resetToDefault}
              />
            }
          />
        )}
      </div>

      {isEmpty ? (
        <ManagePeopleEmptyState />
      ) : (
        <ManagePeopleTable
          columns={columns}
          columnSizing={columnSizing}
          onReorder={onReorder}
          onColumnSizingChange={onColumnSizingChange}
        />
      )}
    </div>
  );
};

const ManagePeoplePage: React.FC = () => {
  const { enterprise, updateBusinessUnits } = useContext(EnterpriseContext);

  return (
    <EnterpriseMembersProvider
      enterprise={enterprise}
      updateBusinessUnits={updateBusinessUnits}
    >
      <ManagePeople />
    </EnterpriseMembersProvider>
  );
};

export default ManagePeoplePage;
